import { memo, useRef } from 'react'
import { Dot, MapPin, TriangleAlert, UsersRound } from 'lucide-react'
import { Event } from '@wellesley/openapi'
import { UserAvatar } from '@wellesley/user'
import { AppsLink } from '@wellesley/router'
import {
  cn,
  HoverCard,
  HoverCardTrigger,
  HoverCardPortal,
  HoverCardContent,
  TooltipWrapper,
} from '@wellesley/ui'
import useIsOverflow from 'hooks/useIsOverflow'
import { getUserImage } from 'helpers/getUserImage'
import { getUserNameWithDomain } from 'helpers/getUserNameWithDomain'
import UserInfoPopup from 'components/UserInfoPopup'
import { EventCardDataRow } from '../EventCardDataRow'
import { EventCover } from '../EventCover'
import { DateTimeDisplay } from '../DateTimeDisplay'
import { AddressDisplay } from '../AddressDisplay'
import { AttendeeActions } from '../AttendeeActions'
import { AttendeesField } from '../AttendeesField'
import { OrganizerActions } from '../OrganizerActions'
import { useEventRoutes } from '../../helpers/Events.routes'
import { useEventsContext } from '../../helpers/Events.context'
import { EVENT_STATS } from '../../helpers/Events.utils'
import { useEventActions } from '../../hooks/useEventActions'
import { useEventTranslation } from '../../hooks/useEventTranslation'
import { useEventUrl } from '../../hooks/useEventUrl'

export const EventCard = memo<Event>((event) => {
  const pathTo = useEventRoutes()
  const eventContext = useEventsContext()

  const titleRef = useRef<HTMLDivElement>(null)
  const isOverflownTitle = useIsOverflow(titleRef)

  const {
    id,
    startTime,
    endTime,
    timeZoneOffsetSeconds,
    organizer,
    url,
    address,
    stats,
    cover,
    attendeeStatus,
  } = event

  const {
    isOrganizer,
    isInviteOnly,
    isPublic,
    isCancelled,
    organizerActions,
    attendeeActions,
    coverBadges,
  } = useEventActions({
    event,
    ...eventContext,
  })

  const organizerUsername = getUserNameWithDomain(organizer)
  const eventRoute = pathTo({
    to: 'event',
    useFromScope: ['group'],
    routeProps: {
      eventId: id,
      username: organizerUsername,
    },
  })

  const urlMeta = useEventUrl(url)
  const { name, TranslationControls } = useEventTranslation(event)

  return (
    <div data-testid="event-card" className="relative border-b py-l">
      <AppsLink
        to={eventRoute}
        aria-label={`Open event ${name}`}
        className="absolute inset-0 z-[1]"
      />

      <div
        className={cn(
          'flex items-start space-x-xl',
          '[&_:is(a,button)]:relative [&_:is(a,button)]:z-[1]',
        )}
      >
        <div className="flex-1 space-y-xxs overflow-hidden">
          <DateTimeDisplay
            className="text-sm font-medium text-gray-500"
            startTime={startTime}
            endTime={endTime}
            timeZoneOffsetSeconds={timeZoneOffsetSeconds}
          />

          <EventCardDataRow>
            <TooltipWrapper
              theme="dark"
              size="small"
              tooltipText={isOverflownTitle ? name : ''}
              triggerProps={{
                asChild: true,
              }}
              className="max-w-80 whitespace-normal break-words"
              side="bottom"
            >
              <h5
                ref={titleRef}
                className={cn(
                  'overflow-hidden text-ellipsis whitespace-nowrap text-lg font-semibold text-gray-900',
                  isCancelled && 'line-through',
                )}
              >
                <AppsLink to={eventRoute}>{name}</AppsLink>
              </h5>
            </TooltipWrapper>
          </EventCardDataRow>

          {TranslationControls}

          {isCancelled && (
            <EventCardDataRow
              icon={<TriangleAlert color="var(--red-500)" />}
              testId="event-cancelled"
            >
              <span className="text-red-500">
                This event has been cancelled
              </span>
            </EventCardDataRow>
          )}

          <EventCardDataRow
            icon={
              <UserAvatar
                name={organizer.displayName || ''}
                imgUrl={getUserImage({
                  user: organizer,
                  type: 'avatar',
                })}
              />
            }
            testId="event-organizer"
          >
            <UserInfoPopup username={organizerUsername}>
              <AppsLink
                to="/@/:username"
                props={{ username: organizerUsername }}
                className="hover:underline"
              >
                By {organizer.displayName || ''} {isOrganizer && '(You)'}
              </AppsLink>
            </UserInfoPopup>
          </EventCardDataRow>

          {urlMeta && (
            <EventCardDataRow icon={urlMeta.Icon} testId="event-url">
              <AppsLink
                to={urlMeta.url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                {urlMeta.label}
              </AppsLink>
            </EventCardDataRow>
          )}

          {address && (
            <EventCardDataRow icon={<MapPin />} testId="event-address">
              <AddressDisplay address={address} isCard />
            </EventCardDataRow>
          )}

          {isInviteOnly && (
            <HoverCard>
              <EventCardDataRow
                icon={<UsersRound />}
                testId="event-attendees-invite-only"
              >
                <HoverCardTrigger className="cursor-pointer">
                  <span className="hover:underline">Attendees</span>
                </HoverCardTrigger>
              </EventCardDataRow>
              <HoverCardPortal>
                <HoverCardContent
                  align="start"
                  className="relative max-h-80 w-[400px] overflow-auto p-s"
                >
                  <AttendeesField eventId={event?.id} readOnly />
                </HoverCardContent>
              </HoverCardPortal>
            </HoverCard>
          )}

          {isPublic && (
            <EventCardDataRow icon={<UsersRound />} testId="event-stats-public">
              <span className="inline-flex">
                {stats?.joins || 0} {EVENT_STATS.joins.label}
                <Dot className="size-xxs" />
                {stats?.interests || 0} {EVENT_STATS.interests.label}
              </span>
            </EventCardDataRow>
          )}

          {!!organizerActions?.length && (
            <div className="pt-xs">
              <OrganizerActions actions={organizerActions} />
            </div>
          )}

          {!!attendeeActions?.length && (
            <div className="pt-xs">
              <AttendeeActions
                attendeeStatus={attendeeStatus}
                actions={attendeeActions}
              />
            </div>
          )}
        </div>

        <div className="flex-shrink-0">
          <EventCover
            className="w-[164px] rounded-lg"
            src={cover?.files[0].uri}
          >
            {coverBadges}
          </EventCover>
        </div>
      </div>
    </div>
  )
})

EventCard.displayName = 'EventCard'
